import { useState } from 'react'
import { Check, Copy, Download, FileImage, Palette, Type, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { PressKitAssetCategory } from '@/types'
import type { PressKitAssetWithId } from '../types'

interface PressKitAssetsSectionProps {
  assets: PressKitAssetWithId[]
}

const CATEGORY_LABELS: Record<PressKitAssetCategory, string> = {
  logo: 'Logos',
  color: 'Colors & Brand',
  font: 'Typography',
  photo: 'Photos',
  other: 'Other',
}

const CATEGORY_ICONS: Record<PressKitAssetCategory, React.ElementType> = {
  logo: FileImage,
  color: Palette,
  font: Type,
  photo: FileImage,
  other: Package,
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().match(/^#?([0-9a-fA-F]{6})$/)
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

const CATEGORY_ORDER: PressKitAssetCategory[] = ['logo', 'color', 'font', 'photo', 'other']

function ColorSwatch({ asset }: { asset: PressKitAssetWithId }) {
  const [copied, setCopied] = useState(false)
  const hex = asset.hexCode ?? ''
  const rgb = hexToRgb(hex)
  // Relative luminance per WCAG; light swatches get dark icon, dark get light icon.
  const iconColor = rgb
    ? (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.6
      ? '#000'
      : '#fff'
    : '#fff'

  async function copyHex() {
    if (!hex) return
    try {
      await navigator.clipboard.writeText(hex)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copyHex()}
      aria-label={`Copy ${hex}`}
      className="group flex flex-col overflow-hidden rounded-lg border text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
    >
      <div
        className="relative h-24 w-full"
        style={{ backgroundColor: hex }}
      >
        <span
          className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          style={{ color: iconColor }}
        >
          {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
        </span>
        {copied && (
          <span
            className="absolute top-1.5 right-2 text-[10px] font-medium uppercase tracking-wide"
            style={{ color: iconColor }}
          >
            Copied
          </span>
        )}
      </div>
      <div className="p-3 space-y-0.5 bg-background">
        <p className="text-sm font-medium truncate">{asset.name}</p>
        {asset.description && (
          <p className="text-xs text-muted-foreground truncate">{asset.description}</p>
        )}
        <p className="font-mono text-xs text-muted-foreground">{hex || '—'}</p>
        {rgb && (
          <p className="font-mono text-[11px] text-muted-foreground/80">
            RGB {rgb.r}, {rgb.g}, {rgb.b}
          </p>
        )}
      </div>
    </button>
  )
}

function PhotoRow({ asset }: { asset: PressKitAssetWithId }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:bg-muted/40"
      >
        {asset.fileUrl && (
          <div className="h-14 w-14 shrink-0 rounded-md border overflow-hidden bg-muted">
            <img src={asset.thumbnailUrl ?? asset.fileUrl} alt={asset.name} loading="lazy" className="h-full w-full object-cover" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{asset.name}</p>
          {asset.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{asset.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-0.5">
            {asset.fileName}
            {asset.fileSize > 0 && ` · ${formatFileSize(asset.fileSize)}`}
          </p>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">View</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="pr-8 truncate">{asset.name}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center rounded-md border bg-muted/30 overflow-hidden max-h-[70vh]">
            <img src={asset.fileUrl} alt={asset.name} className="max-h-[70vh] max-w-full object-contain" />
          </div>
          {asset.description && (
            <p className="text-sm text-muted-foreground">{asset.description}</p>
          )}
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span className="truncate">
              {asset.fileName}
              {asset.fileSize > 0 && ` · ${formatFileSize(asset.fileSize)}`}
            </span>
            <Button variant="default" size="sm" asChild className="shrink-0">
              <a href={asset.fileUrl} download={asset.fileName} target="_blank" rel="noopener noreferrer">
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download
              </a>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AssetRow({ asset, category }: { asset: PressKitAssetWithId; category: PressKitAssetCategory }) {
  if (category === 'color') return <ColorSwatch asset={asset} />
  if (category === 'photo') return <PhotoRow asset={asset} />
  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors">
      {category === 'logo' && asset.fileUrl && (
        <div
          className="h-14 w-14 shrink-0 rounded-md border flex items-center justify-center overflow-hidden p-1.5"
          style={{
            backgroundImage:
              'linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%), linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%)',
            backgroundSize: '10px 10px',
            backgroundPosition: '0 0, 5px 5px',
          }}
        >
          <img src={asset.fileUrl} alt={asset.name} className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{asset.name}</p>
        {asset.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{asset.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">
          {asset.fileName}
          {asset.fileSize > 0 && ` · ${formatFileSize(asset.fileSize)}`}
        </p>
      </div>
      <Button variant="outline" size="sm" asChild className="ml-4 shrink-0">
        <a href={asset.fileUrl} download={asset.fileName} target="_blank" rel="noopener noreferrer">
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Download
        </a>
      </Button>
    </div>
  )
}

function groupByTag(items: PressKitAssetWithId[]): { tag: string | null; items: PressKitAssetWithId[] }[] {
  const map = new Map<string | null, PressKitAssetWithId[]>()
  for (const item of items) {
    const key = item.tag?.trim() ? item.tag.trim() : null
    const list = map.get(key) ?? []
    list.push(item)
    map.set(key, list)
  }
  // untagged first, then tags in insertion order
  const out: { tag: string | null; items: PressKitAssetWithId[] }[] = []
  if (map.has(null)) out.push({ tag: null, items: map.get(null)! })
  for (const [tag, list] of map) {
    if (tag !== null) out.push({ tag, items: list })
  }
  return out
}

export function PressKitAssetsSection({ assets }: PressKitAssetsSectionProps) {
  const grouped = CATEGORY_ORDER.reduce<Record<string, PressKitAssetWithId[]>>((acc, cat) => {
    const items = assets.filter((a) => a.category === cat)
    if (items.length > 0) acc[cat] = items
    return acc
  }, {})

  return (
    <section className="space-y-8">
      {Object.entries(grouped).map(([category, items]) => {
        const cat = category as PressKitAssetCategory
        const Icon = CATEGORY_ICONS[cat]
        const tagGroups = groupByTag(items)
        const taggedGroups = tagGroups.filter((g) => g.tag !== null)
        const untagged = tagGroups.find((g) => g.tag === null)?.items ?? []
        const isColor = cat === 'color'

        const renderItems = (list: PressKitAssetWithId[]) =>
          isColor ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {list.map((asset) => <AssetRow key={asset.id} asset={asset} category={cat} />)}
            </div>
          ) : (
            <div className="divide-y rounded-lg border">
              {list.map((asset) => <AssetRow key={asset.id} asset={asset} category={cat} />)}
            </div>
          )

        return (
          <div key={category}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4" />
              {CATEGORY_LABELS[cat]}
            </h3>

            {untagged.length > 0 && renderItems(untagged)}

            {taggedGroups.length > 0 && (
              <Accordion
                type="multiple"
                className={`w-full rounded-lg border ${untagged.length > 0 ? 'mt-3' : ''}`}
              >
                {taggedGroups.map(({ tag, items: tagItems }) => (
                  <AccordionItem key={tag!} value={tag!} className="last:border-b-0 px-4">
                    <AccordionTrigger className="text-sm font-medium">
                      {tag}
                      <span className="ml-auto mr-2 text-xs text-muted-foreground font-normal">{tagItems.length}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      {renderItems(tagItems)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        )
      })}
    </section>
  )
}
